import UAParser from "ua-parser-js";

import { getSiteInfo } from "@/sites";
import { isClient } from "@/utils/browser";
import { getCookie, setCookie } from "@/services/CookiesService";
import { AMPLITUDE_DEVICE_ID_COOKIE } from "@/internals/constants/cookies";
import { sanitizeAmplitudeDeviceId } from "./deviceId";
import { logWarning } from "../DatadogService";

const { domainName } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN);

/**
 * The Amplitude SDK is loaded through a memoized dynamic import so it never
 * ships in the initial JS bundle: the chunk is fetched on first use (init is
 * scheduled on idle by AmplitudeContextProvider, QRWMAU-1152) and every later
 * call reuses the loaded module.
 *
 * `amplitudeGetDeviceId` and `persistAmplitudeDeviceIdCookie` keep their
 * synchronous contract (the payment pick step and the HTTP client rely on it),
 * reading the memoized module: before the chunk has loaded they return
 * undefined and callers fall back to the persisted device-id cookie.
 *
 * Calls that land before `initAmplitude` finishes (e.g. `view_landing_home`
 * on mount) are safe by design: the SDK queues pre-init dispatches in its
 * internal `dispatchQ` and flushes them at the end of `init` (analytics-core
 * `dispatch()` → browser-client Step 6), so they are sent with the final
 * apiKey and `serverZone: "EU"` — never dropped, never sent to the default US
 * zone.
 */
type AmplitudeBrowserModule = typeof import("@amplitude/analytics-browser");
type AmplitudeInitState = "idle" | "pending" | "done" | "error";

let initState: AmplitudeInitState = "idle";
let initPromise: Promise<void> | null = null;
let amplitudeModulePromise: Promise<AmplitudeBrowserModule | null> | null = null;

let loadedAmplitudeModule: AmplitudeBrowserModule | null = null;

// RegExp matches referring host + subdomains (SDK matches hostname, not full URL paths).
const EXCLUDED_REFERRER_DOMAINS: RegExp[] = [
  /(^|\.)qrcreator\.com$/,
  /(^|\.)qrcreator\.dev$/,
  /(^|\.)qrnow\.pro$/,
  /(^|\.)qrnow\.com$/,
  /(^|\.)pay\.nicepay\.co\.kr$/,
  /(^|\.)stripe\.com$/,
  /(^|\.)paypal\.com$/,
];
const loadAmplitudeModule = (): Promise<AmplitudeBrowserModule | null> => {
  if (!isClient()) return Promise.resolve(null);

  if (!amplitudeModulePromise) {
    amplitudeModulePromise = import("@amplitude/analytics-browser")
      .then((mod) => {
        loadedAmplitudeModule = mod;
        return mod;
      })
      .catch((error) => {
        logWarning(error instanceof Error ? error.message : String(error), {
          component: "AmplitudeService",
          action: "loadAmplitudeModule",
        });
        amplitudeModulePromise = null;
        loadedAmplitudeModule = null;
        return null;
      });
  }
  return amplitudeModulePromise;
};

export const initAmplitude = async (): Promise<void> => {
  const amplitudeKey = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY || "";
  if (!amplitudeKey || !isClient()) return;

  //  If it is already ready, we do nothing.
  if (initState === "done") return;

  // If there is a pending initialization, we wait for it.
  if (initState === "pending" && initPromise) return initPromise;

  // If we were in "error" or "idle" state, we allow starting/retrying.
  initState = "pending";

  initPromise = (async () => {
    try {
      const sdk = await loadAmplitudeModule();
      if (!sdk) {
        initState = "error";
        initPromise = null;
        return;
      }

      const initResult = sdk.init(amplitudeKey, undefined, {
        serverZone: "EU",
        cookieOptions: {
          domain: `.${domainName}`,
          expiration: 365,
          sameSite: "Lax",
          secure: true,
        },
        autocapture: {
          attribution: {
            excludeInternalReferrers: true,
            excludeReferrers: EXCLUDED_REFERRER_DOMAINS,
            resetSessionOnNewCampaign: true,
          },
          pageViews: false,
          sessions: false,
          formInteractions: false,
          fileDownloads: false,
          pageUrlEnrichment: true,
          elementInteractions: false,
          webVitals: false,
        },
      });

      await initResult?.promise?.catch(() => undefined);
      await amplitudeDeviceType().catch(() => undefined);
      persistAmplitudeDeviceIdCookie();

      initState = "done";
    } catch (error) {
      initState = "error";
      initPromise = null;
      logWarning(error instanceof Error ? error.message : String(error), {
        component: "AmplitudeService",
        action: "initAmplitude",
      });
    }
  })();

  return initPromise;
};

export const getAmplitudeInitState = (): AmplitudeInitState => initState;
export const isAmplitudeInitialized = (): boolean => initState === "done";

export const amplitudeDeviceType = async (): Promise<void> => {
  const sdk = await loadAmplitudeModule();
  if (!sdk) return;

  const parser = new UAParser();
  const userAgent = parser.getResult();

  let deviceType = "Desktop";
  if (userAgent.device.type === "mobile") deviceType = "Mobile";
  else if (userAgent.device.type === "tablet") deviceType = "Tablet";

  const identifyEvent = new sdk.Identify();
  identifyEvent.set("User Device", deviceType);

  sdk.identify(identifyEvent);
};
export const amplitudeTrackEvent = (
  name: string,
  data?: Record<string, string | number | boolean>
): void => {
  if (process.env.NEXT_PUBLIC_ENV === "local" || !isClient()) return;

  void (async () => {
    await initAmplitude();
    if (initState !== "done") return;

    const sdk = await loadAmplitudeModule();
    if (!sdk) return;

    try {
      const identifyEvent = new sdk.Identify();
      identifyEvent.set("domain", domainName);
      sdk.identify(identifyEvent);
      sdk.track(name, { ...data });
    } catch (error) {
      logWarning(error instanceof Error ? error.message : String(error), {
        component: "AmplitudeService",
        action: "amplitudeTrackEvent",
      });
    }
  })();
};

export const amplitudeTrackUser = (userId: string): void => {
  if (process.env.NEXT_PUBLIC_ENV === "local" || !isClient()) return;

  void (async () => {
    await initAmplitude();

    if (initState !== "done") return;

    const sdk = await loadAmplitudeModule();
    if (!sdk) return;

    try {
      sdk.setUserId(userId);
      const ident = new sdk.Identify().set("User Id", userId);
      sdk.identify(ident);
    } catch (error) {
      logWarning(error instanceof Error ? error.message : String(error), {
        component: "AmplitudeService",
        action: "amplitudeTrackUser",
      });
    }
  })();
};

export const amplitudeLogoutUser = async (): Promise<void> => {
  if (!isClient()) return;
  const sdk = await loadAmplitudeModule();
  if (!sdk) return;

  await sdk.flush().promise.catch(() => undefined);
  sdk.setUserId(undefined);
};

// Cache interna solo para cuando el SDK está disponible o para sincronización
let cachedDeviceId: string | undefined = undefined;

export const amplitudeGetDeviceId = (): string | undefined => {
  if (process.env.NEXT_PUBLIC_ENV === "local" || !isClient()) return undefined;

  if (loadedAmplitudeModule?.getDeviceId) {
    const sdkDeviceId = loadedAmplitudeModule.getDeviceId();
    if (sdkDeviceId) {
      cachedDeviceId = sanitizeAmplitudeDeviceId(sdkDeviceId);
      return cachedDeviceId;
    }
  }

  if (cachedDeviceId) {
    return cachedDeviceId;
  }

  return sanitizeAmplitudeDeviceId(getCookie(AMPLITUDE_DEVICE_ID_COOKIE));
};

const AMPLITUDE_DEVICE_ID_COOKIE_TTL_MS = 365 * 24 * 60 * 60 * 1000;

export const persistAmplitudeDeviceIdCookie = (): string | undefined => {
  if (!isClient()) return undefined;

  const deviceId =
    sanitizeAmplitudeDeviceId(amplitudeGetDeviceId()) ??
    sanitizeAmplitudeDeviceId(getCookie(AMPLITUDE_DEVICE_ID_COOKIE));

  if (!deviceId) return undefined;

  cachedDeviceId = deviceId; // Mantenemos la cache sincronizada

  try {
    setCookie(
      AMPLITUDE_DEVICE_ID_COOKIE,
      deviceId,
      new Date(Date.now() + AMPLITUDE_DEVICE_ID_COOKIE_TTL_MS)
    );
  } catch {}

  return deviceId;
};
// ============================================================================
// TEST UTILITIES (exported only for testing, never use in production code)
// ============================================================================

/**
 * Reset Amplitude initialization state to "idle". Used by tests to ensure
 * each test starts with a clean slate and doesn't affect subsequent tests.
 *
 * @internal - Test-only utility
 */
export const __testResetAmplitudeState__ = (): void => {
  initState = "idle";
  initPromise = null;
  amplitudeModulePromise = null;
  loadedAmplitudeModule = null;
  cachedDeviceId = undefined;
};

/**
 * Warm the memoized Amplitude module so synchronous callers (like the payment
 * pick step and HTTP client) can read it immediately. Used by tests that need
 * the SDK module to be loaded before calling synchronous getters.
 *
 * After calling this, `amplitudeModule` is populated (or null if load failed),
 * and `amplitudeGetDeviceId()` can return a value without async/await.
 *
 * @internal - Test-only utility
 * @returns Promise that resolves when the module is loaded or failed
 */
export const __testWarmAmplitudeModule__ = async (): Promise<void> => {
  await loadAmplitudeModule();
};
